import { useApi } from "api/hooks/useApi";
import { useCallback, useEffect, useRef, useState } from "react";

export interface Notification {
  id: string;
  postId: string;
  commentId: string;
  message: string;
}

export function useNotifications(pollInterval = 30000) {
  const { request } = useApi();
  const requestRef = useRef(request);
  const [notifications, setNotifications] = useState<Notification[]>([]);
  const [loading, setLoading] = useState(false);
  const isFetching = useRef(false);

  const unreadCount = notifications.length;

  const fetchNotifications = useCallback(async () => {
    if (!localStorage.getItem("accessToken")) return;
    if (isFetching.current) return;
    isFetching.current = true;
    try {
      setLoading(true);
      const res = await requestRef.current<{ response: Notification[] }>(
        "api/notifications",
        { headers: { "Cache-Control": "no-cache" } }
      );
      setNotifications(res.response || []);
    } finally {
      setLoading(false);
      isFetching.current = false;
    }
  }, []);

  const markAsRead = useCallback(async (id: string) => {
    await requestRef.current(`api/notifications/${id}/read`, { method: "PUT" });
    setNotifications((prev) => prev.filter((n) => n.id !== id));
  }, []);

  const markPostAsRead = useCallback(async (postId: string) => {
    await requestRef.current(`api/notifications/posts/${postId}/read`, { method: "PUT" });
    setNotifications((prev) => prev.filter((n) => n.postId !== postId));
  }, []);

  useEffect(() => {
    fetchNotifications();

    const intervalId = setInterval(() => {
      if (document.visibilityState === "visible") {
        fetchNotifications();
      }
    }, pollInterval);

    const handleVisibilityChange = () => {
      if (document.visibilityState === "visible") {
        fetchNotifications();
      }
    };

    document.addEventListener("visibilitychange", handleVisibilityChange);

    return () => {
      clearInterval(intervalId);
      document.removeEventListener("visibilitychange", handleVisibilityChange);
    };
  }, [fetchNotifications, pollInterval]);

  return {
    notifications,
    unreadCount,
    loading,
    fetchNotifications,
    markAsRead,
    markPostAsRead,
  };
}
